import React from "react";
import { useTranslation } from "react-i18next";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TripOriginRoundedIcon from "@mui/icons-material/TripOriginRounded";
import {
  MeHailingOrderService,
  TripHistoryOrder,
} from "../../../api/meHailingOrder.service";
import { getPickupTime } from "../tracker/tripStatus.helper";

const PAGE_SIZE = 10;

type TripStage =
  | "completed"
  | "cancelled"
  | "timedOut"
  | "pending"
  | "accepted"
  | "approaching"
  | "arrived"
  | "onGoing"
  | "inProgress";

const stageOf = (status: string): TripStage => {
  switch (status.toUpperCase()) {
    case "COMPLETED":
      return "completed";
    case "CANCELLED":
      return "cancelled";
    case "TIMED_OUT":
      return "timedOut";
    case "PENDING":
      return "pending";
    case "ACCEPTED":
    case "CHECK_IN":
      return "accepted";
    case "APPROACHING":
      return "approaching";
    case "ARRIVED":
      return "arrived";
    case "ON_GOING":
      return "onGoing";
    default:
      return "inProgress";
  }
};

const STAGE_STYLE: Partial<Record<TripStage, string>> = {
  completed: "bg-[#E7F6EC] text-[#1E7B3C]",
  cancelled: "bg-[#FDECEC] text-[#A12622]",
  timedOut: "bg-[#ECEDEE] text-[#535A60]",
};
const LIVE_STAGE_STYLE = "bg-[#EAF3FB] text-brand-blue";

export interface OpenTrip {
  hailingRequestId: string;
  /** The ride's latest known status, fresher than the history page. */
  status?: string | undefined;
  pickupPlace: string;
  dropoffPlace: string;
  /** Pickup time of a scheduled ride (ISO); null for a live one. */
  scheduledTime: string | null;
  vehicleName: string;
  fare: string;
}

const RouteLines: React.FC<{ from: string; to: string }> = ({ from, to }) => (
  <div className="flex flex-col">
    <div className="flex items-center gap-2">
      <TripOriginRoundedIcon sx={{ color: "#1479c7", fontSize: 18 }} />
      <span className="text-base font-semibold leading-tight text-mineshaft-900">{from}</span>
    </div>
    <div className="ml-[8px] h-3 border-l-2 border-dashed border-gray-400" />
    <div className="flex items-center gap-2">
      <LocationOnIcon sx={{ color: "#22b752", fontSize: 20, ml: "-1px" }} />
      <span className="text-base font-semibold leading-tight text-mineshaft-900">{to}</span>
    </div>
  </div>
);

/**
 * A ride still under way: whether it is live or scheduled and when, where it is up to,
 * the route and the fare. The whole card opens its tracker.
 */
const OpenTripCard: React.FC<{ trip: OpenTrip; onOpen: () => void }> = ({ trip, onOpen }) => {
  const { t } = useTranslation("hailing");
  const scheduled = Boolean(trip.scheduledTime);
  const status = (trip.status ?? "PENDING").toUpperCase();
  // A scheduled ride still waiting for its driver is booked, not being searched for.
  const statusLabel =
    scheduled && status === "PENDING"
      ? t("trips.history.status.booked")
      : t(`trips.history.status.${stageOf(status)}`);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t("trips.history.openTrip", { from: trip.pickupPlace, to: trip.dropoffPlace })}
      className="flex flex-col gap-3 rounded-lg border-[1.5px] border-brand-blue bg-white p-4 text-left transition-shadow hover:shadow-[0_4px_16px_rgba(15,21,26,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
    >
      <span className="flex w-full items-center justify-between gap-3">
        <span
          className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            scheduled ? "bg-[#F1ECFB] text-[#5B3BB5]" : "bg-[#EAF3FB] text-brand-blue"
          }`}
        >
          <span aria-hidden="true" className="font-material-symbols text-[14px] leading-none">
            {scheduled ? "event" : "bolt"}
          </span>
          {t(scheduled ? "trips.history.scheduled" : "trips.history.live")}
        </span>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${LIVE_STAGE_STYLE}`}>
          {statusLabel}
        </span>
      </span>

      <span className="text-sm font-semibold text-mineshaft-900">
        {scheduled
          ? t("trips.history.pickupAt", { time: getPickupTime(t, trip.scheduledTime) })
          : t("trips.history.pickupNow")}
      </span>

      <RouteLines from={trip.pickupPlace} to={trip.dropoffPlace} />

      <span className="flex w-full items-center justify-between gap-3 border-t border-gray-200 pt-3 text-sm">
        <span className="min-w-0 truncate text-mineshaft-700">
          {[trip.vehicleName, trip.fare].filter(Boolean).join(" · ")}
        </span>
        <span className="shrink-0 font-semibold text-brand-blue">
          {t("trips.history.viewTrip")} ›
        </span>
      </span>
    </button>
  );
};

export interface TripHistoryProps {
  /** The rider's rides that have not ended yet (there can be several): they come first and open. */
  openTrips?: OpenTrip[];
  onOpenTrip?: (hailingRequestId: string) => void;
}

/**
 * My trips: the rider's rides, one card each — when, route, status, driver and vehicle,
 * fare and reference. Rides still under way come first and open their live tracker. Read from GET /v3/me/transaction-history,
 * the history the user app also reads.
 */
const TripHistory: React.FC<TripHistoryProps> = ({ openTrips = [], onOpenTrip }) => {
  const { t, i18n } = useTranslation("hailing");
  const isZh = i18n.language.startsWith("zh");
  const [trips, setTrips] = React.useState<TripHistoryOrder[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);

  const loadPage = React.useCallback(async (skip: number) => {
    setLoading(true);
    setLoadError(false);
    try {
      const page = await new MeHailingOrderService().getTripHistory({
        take: PAGE_SIZE,
        skip,
      });
      setTrips((previous) => (skip === 0 ? page.items : [...previous, ...page.items]));
      setTotal(page.total);
    } catch (error) {
      console.error("Failed to load the trip history.", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadPage(0);
  }, [loadPage]);

  // A ride that just ended leaves the open rides: reload the history so it shows
  // among the past trips with its final status and fare.
  const openIds = openTrips.map((trip) => trip.hailingRequestId).join(",");
  const previousOpenIds = React.useRef(openIds);
  React.useEffect(() => {
    const before = previousOpenIds.current.split(",").filter(Boolean);
    previousOpenIds.current = openIds;
    const now = new Set(openIds.split(","));
    if (before.some((id) => !now.has(id))) void loadPage(0);
  }, [loadPage, openIds]);

  const money = React.useMemo(
    () =>
      new Intl.NumberFormat(isZh ? "zh-HK" : "en-HK", {
        style: "currency",
        currency: "HKD",
        maximumFractionDigits: 2,
      }),
    [isZh],
  );
  const dateTime = React.useMemo(
    () =>
      new Intl.DateTimeFormat(isZh ? "zh-HK" : "en-HK", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    [isZh],
  );

  const placeName = (point: NonNullable<TripHistoryOrder["itinerary"]>[number] | undefined) =>
    (isZh
      ? point?.i18n?.zhHK?.displayName || point?.i18n?.en?.displayName
      : point?.i18n?.en?.displayName || point?.i18n?.zhHK?.displayName) || "—";

  // Rides under way get their own section, so they are left out of the past trips.
  const isOpen = (trip: TripHistoryOrder) =>
    openTrips.some((open) => open.hailingRequestId === trip.hailingRequestId);
  const pastTrips = trips.filter((trip) => !isOpen(trip));
  // Live rides first, then scheduled ones soonest first.
  const upcoming = [...openTrips].sort((a, b) => {
    if (!a.scheduledTime || !b.scheduledTime) {
      return Number(Boolean(a.scheduledTime)) - Number(Boolean(b.scheduledTime));
    }
    return Date.parse(a.scheduledTime) - Date.parse(b.scheduledTime);
  });

  return (
    <section aria-labelledby="trip-history-heading" className="flex flex-col gap-3">
      <h2 id="trip-history-heading" className="text-xl font-bold text-mineshaft-900">
        {t("trips.history.title")}
      </h2>

      {onOpenTrip && upcoming.length > 0 ? (
        <div className="flex flex-col gap-3">
          <h3 className="text-[13px] font-semibold uppercase tracking-wide text-mineshaft-700">
            {t("trips.history.upcomingTitle", { count: upcoming.length })}
          </h3>
          {upcoming.map((open) => (
            <OpenTripCard
              key={open.hailingRequestId}
              trip={open}
              onOpen={() => onOpenTrip(open.hailingRequestId)}
            />
          ))}
          {pastTrips.length > 0 || loading ? (
            <h3 className="mt-3 text-[13px] font-semibold uppercase tracking-wide text-mineshaft-700">
              {t("trips.history.pastTitle")}
            </h3>
          ) : null}
        </div>
      ) : null}

      {pastTrips.map((trip) => {
        const stage = stageOf(trip.status);
        const itinerary = trip.itinerary ?? [];
        const when = trip.request?.time ?? trip.createdAt;
        // What the rider was charged. `tripTotal` is the meter fare alone (no boost or fees),
        // so it is only the last resort.
        const fare = trip.userTotal ?? trip.billing?.total ?? trip.billing?.tripTotal;
        const driverName = isZh
          ? trip.driver?.nameTc || trip.driver?.name
          : trip.driver?.name || trip.driver?.nameTc;
        const vehicle = [
          trip.vehicle?.licensePlate,
          [trip.vehicle?.make, trip.vehicle?.model].filter(Boolean).join(" "),
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <article
            key={trip.id}
            aria-label={t("trips.history.tripCard", {
              from: placeName(itinerary[0]),
              to: placeName(itinerary[itinerary.length - 1]),
            })}
            className="flex flex-col gap-3 rounded-lg bg-white p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-mineshaft-700">
                {when ? dateTime.format(new Date(when)) : ""}
              </span>
              <span
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  STAGE_STYLE[stage] ?? LIVE_STAGE_STYLE
                }`}
              >
                {t(`trips.history.status.${stage}`)}
              </span>
            </div>

            <RouteLines
              from={placeName(itinerary[0])}
              to={itinerary.length > 1 ? placeName(itinerary[itinerary.length - 1]) : "—"}
            />

            <div className="flex items-end justify-between gap-3 border-t border-gray-200 pt-3">
              <div className="min-w-0 text-sm text-mineshaft-700">
                <p className="font-semibold text-mineshaft-900">
                  {driverName || t("trips.history.noDriver")}
                </p>
                {vehicle ? <p>{vehicle}</p> : null}
                <p className="truncate">
                  {t("trips.history.reference", { id: trip.hailingRequestId ?? trip.id })}
                </p>
              </div>
              {fare != null ? (
                <span className="shrink-0 text-lg font-bold text-mineshaft-900">
                  {money.format(fare)}
                </span>
              ) : null}
            </div>
          </article>
        );
      })}

      {loading ? (
        <p role="status" className="text-sm text-mineshaft-700">
          {t("trips.history.loading")}
        </p>
      ) : null}

      {loadError ? (
        <div role="alert" className="flex items-center justify-between gap-3 text-sm text-[#A12622]">
          <span>{t("trips.history.loadError")}</span>
          <button
            type="button"
            onClick={() => void loadPage(trips.length)}
            className="min-h-11 font-semibold text-brand-blue"
          >
            {t("trips.history.retry")}
          </button>
        </div>
      ) : null}

      {!loading && !loadError && pastTrips.length === 0 && openTrips.length === 0 ? (
        <p className="rounded-lg bg-white p-6 text-center text-sm text-mineshaft-700">
          {t("trips.empty")}
        </p>
      ) : null}

      {!loading && !loadError && trips.length < total ? (
        <button
          type="button"
          onClick={() => void loadPage(trips.length)}
          className="min-h-11 rounded-full border-[1.5px] border-brand-blue bg-white font-semibold text-brand-blue"
        >
          {t("trips.history.loadMore")}
        </button>
      ) : null}
    </section>
  );
};

export default TripHistory;
